// Наблюдаемый запрос к локальной базе — единая точка входа приложения.
//
// Зачем обёртка. Голый `useQuery` из @powersync/react перезапрашивает выборку и
// отдаёт НОВУЮ ссылку на массив при любом изменении зависимых таблиц — даже
// когда результат тот же. Каждый такой прогон перерисовывает всех подписчиков:
// на 156 наблюдаемых запросов приложения это заметная работа, и на Capacitor
// она дороже, чем в вебе — результат едет через нативный мост сериализованным,
// а разбирается в главном потоке.
//
// SDK умеет это гасить (`rowComparator`), но параметр надо передавать в каждом
// вызове, и до сих пор его не передавал НИ ОДИН из 156. Поэтому умолчание
// задаётся здесь, а не расставляется по местам.
//
// Как устроено сравнение — и почему именно так. Сравнитель обязан быть
// консервативным: если он «не заметит» изменение, экран молча покажет старое.
// Это хуже лишней перерисовки, поэтому:
//   • ключ строки — `id` (он есть у всех синкаемых таблиц);
//   • признак изменения — `updated_at`, когда он в выборке есть. Колонка
//     обязательна для синкаемых таблиц и поддерживается триггером
//     (rules/cross-platform-server.md), то есть меняется при ЛЮБОЙ правке;
//   • если `updated_at` в выборке нет (агрегаты, срезы колонок) — честный
//     `JSON.stringify` всей строки. Дороже, зато пропустить изменение не может.
//
// Почему не всегда `JSON.stringify`: выборки вида `SELECT * FROM materials`
// тянут полный текст документа, и сериализация сорока таких строк на каждое
// изменение таблицы стоила бы дороже той перерисовки, которую мы убираем.

import { useQuery } from "@powersync/react";

/**
 * Опции наблюдаемого запроса. Тип SDK (`DifferentialHookOptions`) из корня
 * пакета не экспортируется, поэтому описываем ровно то, чем пользуемся, —
 * иначе пришлось бы импортировать из внутреннего пути и ловить поломку при
 * следующем обновлении.
 */
interface WatchOptions<RowType> {
	rowComparator?: { keyBy: (row: RowType) => string; compareBy: (row: RowType) => string };
	runQueryOnce?: boolean;
	throttleMs?: number;
	streams?: unknown[];
	reportFetching?: boolean;
}

interface RowLike {
	id?: unknown;
	updated_at?: unknown;
}

/** Умолчание сравнения. Экспортируется для тестов и для точечных переопределений. */
export const DEFAULT_ROW_COMPARATOR = {
	keyBy: (row: RowLike): string => String(row.id ?? JSON.stringify(row)),
	compareBy: (row: RowLike): string =>
		row.updated_at === undefined
			? JSON.stringify(row)
			: `${String(row.id)}|${String(row.updated_at)}`,
};

/**
 * Наблюдаемый запрос к локальной базе.
 *
 * Сигнатура повторяет `useQuery` из SDK. Отличие одно: если вызывающий не задал
 * свой `rowComparator`, подставляется умолчание выше — эмиссия происходит
 * только при реальном изменении результата, а неизменившиеся строки сохраняют
 * прежние ссылки (важно для `useMemo` у потребителей).
 *
 * Свой сравнитель имеет смысл передавать там, где строка меняется без
 * `updated_at` (например, вычисляемые поля в SELECT).
 */
export function usePowerSyncQuery<RowType = unknown>(
	sql: string,
	parameters?: unknown[],
	options?: WatchOptions<RowType>,
): ReturnType<typeof useQuery<RowType>> {
	return useQuery<RowType>(sql, parameters, {
		rowComparator: DEFAULT_ROW_COMPARATOR as WatchOptions<RowType>["rowComparator"],
		...options,
	} as Parameters<typeof useQuery<RowType>>[2]);
}
